"use client";

import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import AnimatedSection from "./AnimatedSection";
import { Project, accentColors } from "@/data/projects";

interface CaseStudyProps {
  project: Project;
}

const CaseStudy = ({ project }: CaseStudyProps) => {
  const colors = accentColors[project.accent];
  const shots = project.screenshots;
  const [lightbox, setLightbox] = useState<number | null>(null);

  // Lock scroll + wire up Escape / arrow-key navigation while the lightbox is open.
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowLeft") setLightbox((i) => (i === null ? i : (i - 1 + shots.length) % shots.length));
      else if (e.key === "ArrowRight") setLightbox((i) => (i === null ? i : (i + 1) % shots.length));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, shots.length]);

  return (
    <main className="dot-grid min-h-screen bg-[#0A0A0F] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 z-50 w-full border-b border-white/[0.06] bg-[#0A0A0F]/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center px-4 py-4">
          <Link
            href="/#work"
            className="flex items-center gap-2 text-sm text-[#94A3B8] transition-colors hover:text-white"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M13 8H3M3 8L7 4M3 8L7 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back to Portfolio
          </Link>
        </div>
      </nav>

      <div className="mx-auto max-w-4xl px-4 pt-28 pb-24">
        {/* Project header */}
        <AnimatedSection>
          <div className="mb-12">
            <div className="mb-4 flex items-center gap-3">
              <motion.h1
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="font-[family-name:var(--font-space-grotesk)] text-4xl font-bold md:text-5xl"
              >
                {project.name}
              </motion.h1>
              {project.status === "live" ? (
                <span className="flex items-center gap-1.5 text-xs text-emerald-400">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  </span>
                  Live
                </span>
              ) : project.status === "private" ? (
                <span className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs text-[#94A3B8]">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Private
                </span>
              ) : (
                <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-[#94A3B8]">
                  Coming Soon
                </span>
              )}
            </div>

            {/* Project image */}
            {project.image && (
              <div className="mb-8 flex items-center justify-center rounded-xl bg-white/[0.03] p-6 ring-1 ring-white/[0.06]">
                <Image
                  src={project.image}
                  alt={`${project.name} logo`}
                  width={300}
                  height={150}
                  className="h-24 w-auto object-contain"
                />
              </div>
            )}

            {/* Accent line */}
            <div className={`mb-8 h-1 w-16 rounded-full bg-gradient-to-r ${project.gradient}`} />

            {/* Long description */}
            <p className="text-lg leading-relaxed text-[#94A3B8]">
              {project.longDescription}
            </p>

            {/* Timeline metric */}
            {project.timeline && (
              <p className={`mt-6 inline-flex items-center gap-2 rounded-full bg-white/[0.03] px-4 py-1.5 text-sm font-medium ${colors.text} ring-1 ring-white/[0.06] font-[family-name:var(--font-jetbrains-mono)]`}>
                {project.timeline}
              </p>
            )}

            {/* CTAs: live site + (BidFlow only) the 3D architecture flythrough */}
            {(project.url || project.slug === "bidflow") && (
              <div className="mt-6 flex flex-wrap gap-3">
                {project.url && (
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 rounded-full bg-white/5 px-5 py-2.5 text-sm font-medium ${colors.text} ring-1 ring-white/10 transition-all hover:bg-white/10`}
                  >
                    Visit live site
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M6 3H3V13H13V10M9 3H13V7M13 3L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </a>
                )}
                {project.slug === "bidflow" && (
                  <Link
                    href="/anatomy"
                    className={`inline-flex items-center gap-2 rounded-full bg-white/5 px-5 py-2.5 text-sm font-medium ${colors.text} ring-1 ring-white/10 transition-all hover:bg-white/10`}
                  >
                    Explore the architecture
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M8 2v12M8 14l-4-4M8 14l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Link>
                )}
              </div>
            )}
          </div>
        </AnimatedSection>

        {/* Featured: live guided product flow (spotlighted near the top) */}
        {project.guidedFlow && (
          <AnimatedSection delay={0.03}>
            <div className={`mb-12 overflow-hidden rounded-2xl bg-gradient-to-br ${project.gradient} p-[1px]`}>
              <div className="rounded-2xl bg-[#0A0A0F] p-6 sm:p-8">
                <h2 className={`mb-3 inline-flex items-center gap-2 font-[family-name:var(--font-space-grotesk)] text-sm font-medium uppercase tracking-[0.2em] ${colors.text}`}>
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
                  </span>
                  See it live
                </h2>
                <p className="mb-5 max-w-2xl text-base leading-relaxed text-white/85">
                  {project.guidedFlow.blurb}
                </p>
                <a
                  href={project.guidedFlow.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 rounded-full bg-gradient-to-r ${project.gradient} px-6 py-3 text-sm font-semibold text-[#0A0A0F] transition-transform hover:scale-[1.03]`}
                >
                  {project.guidedFlow.label}
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                    <path d="M6 3H3V13H13V10M9 3H13V7M13 3L7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* The Problem */}
        {project.problem && (
          <AnimatedSection delay={0.05}>
            <div className="mb-12">
              <h2 className="mb-4 font-[family-name:var(--font-space-grotesk)] text-sm font-medium uppercase tracking-[0.2em] text-white/50">
                The Problem
              </h2>
              <p className={`border-l-2 pl-5 text-base leading-relaxed text-white/80 ${project.accent === "emerald" ? "border-emerald-500/40" : project.accent === "teal" ? "border-teal-500/40" : project.accent === "lime" ? "border-lime-500/40" : project.accent === "violet" ? "border-violet-500/40" : project.accent === "amber" ? "border-amber-500/40" : project.accent === "pink" ? "border-pink-500/40" : project.accent === "sky" ? "border-sky-500/40" : "border-white/20"}`}>
                {project.problem}
              </p>
            </div>
          </AnimatedSection>
        )}

        {/* Tech stack */}
        <AnimatedSection delay={0.1}>
          <div className="mb-12">
            <h2 className="mb-4 font-[family-name:var(--font-space-grotesk)] text-sm font-medium uppercase tracking-[0.2em] text-white/50">
              Tech Stack
            </h2>
            <div className="flex flex-wrap gap-2">
              {project.tech.map((t) => (
                <span
                  key={t}
                  className={`rounded-full px-4 py-1.5 text-sm ring-1 ${colors.badge}`}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Architecture */}
        {project.architecture && project.architecture.length > 0 && (
          <AnimatedSection delay={0.15}>
            <div className="mb-12">
              <h2 className="mb-4 font-[family-name:var(--font-space-grotesk)] text-sm font-medium uppercase tracking-[0.2em] text-white/50">
                Architecture
              </h2>
              <div className="space-y-3">
                {project.architecture.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-xl bg-white/[0.02] p-4 ring-1 ring-white/[0.06]"
                  >
                    <span className={`mt-0.5 shrink-0 font-mono text-xs ${colors.text}`}>
                      0{i + 1}
                    </span>
                    <p className="text-sm leading-relaxed text-[#94A3B8]">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* Features */}
        <AnimatedSection delay={0.2}>
          <div className="mb-12">
            <h2 className="mb-4 font-[family-name:var(--font-space-grotesk)] text-sm font-medium uppercase tracking-[0.2em] text-white/50">
              Key Features
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              {project.features.map((feature) => (
                <div
                  key={feature}
                  className="flex items-start gap-3 rounded-xl bg-white/[0.02] p-4 ring-1 ring-white/[0.06]"
                >
                  <span className={`mt-0.5 ${colors.text}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8L6.5 11.5L13 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="text-sm text-[#94A3B8]">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Code excerpts */}
        {project.codeExcerpts && project.codeExcerpts.length > 0 && (
          <AnimatedSection delay={0.25}>
            <div className="mb-12">
              <h2 className="mb-4 font-[family-name:var(--font-space-grotesk)] text-sm font-medium uppercase tracking-[0.2em] text-white/50">
                Code Highlights
              </h2>
              <div className="space-y-6">
                {project.codeExcerpts.map((excerpt, i) => (
                  <div key={i} className="overflow-hidden rounded-xl bg-white/[0.02] ring-1 ring-white/[0.06]">
                    <div className="border-b border-white/[0.06] px-5 py-3">
                      <h3 className={`text-sm font-semibold ${colors.text}`}>
                        {excerpt.title}
                      </h3>
                      {excerpt.file && (
                        <p className="mt-0.5 text-[11px] text-white/40 font-[family-name:var(--font-jetbrains-mono)]">
                          {excerpt.file}
                        </p>
                      )}
                      <p className="mt-2 text-sm leading-relaxed text-[#94A3B8]">
                        {excerpt.caption}
                      </p>
                    </div>
                    <pre className="overflow-x-auto px-5 py-4 text-[12px] leading-relaxed text-white/85 font-[family-name:var(--font-jetbrains-mono)]">
                      <code>{excerpt.code}</code>
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* AI workflow */}
        {project.aiWorkflow && (
          <AnimatedSection delay={0.3}>
            <div className="mb-12 overflow-hidden rounded-xl bg-gradient-to-br from-white/[0.04] to-transparent p-6 ring-1 ring-white/[0.06]">
              <h2 className={`mb-4 inline-flex items-center gap-2 font-[family-name:var(--font-space-grotesk)] text-sm font-medium uppercase tracking-[0.2em] ${colors.text}`}>
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
                How I Built This With Claude
              </h2>
              <p className="text-base leading-relaxed text-white/85">
                {project.aiWorkflow}
              </p>
            </div>
          </AnimatedSection>
        )}

        {/* Screenshots */}
        <AnimatedSection delay={0.3}>
          <div className="mb-12">
            <h2 className="mb-4 font-[family-name:var(--font-space-grotesk)] text-sm font-medium uppercase tracking-[0.2em] text-white/50">
              Screenshots
            </h2>
            {shots.length > 0 ? (
              <>
                <p className="mb-4 text-sm text-white/40">
                  Tap any screenshot to view it full size.
                </p>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5">
                  {shots.map((src, i) => (
                    <motion.button
                      key={i}
                      type="button"
                      onClick={() => setLightbox(i)}
                      whileHover={{ y: -4, scale: 1.03 }}
                      transition={{ duration: 0.2 }}
                      className="group relative aspect-[9/19] cursor-zoom-in overflow-hidden rounded-lg bg-[#0f0f14] ring-1 ring-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                      aria-label={`View ${project.name} screenshot ${i + 1} full size`}
                    >
                      <Image
                        src={src}
                        alt={`${project.name} screenshot ${i + 1}`}
                        width={400}
                        height={869}
                        className="h-full w-full object-cover object-top"
                        loading="lazy"
                        unoptimized
                      />
                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/30 group-hover:opacity-100">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white drop-shadow">
                          <circle cx="11" cy="11" r="7" />
                          <path d="m21 21-4.35-4.35M11 8v6M8 11h6" />
                        </svg>
                      </span>
                    </motion.button>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center rounded-xl border border-dashed border-white/[0.08] bg-white/[0.01] py-16">
                <p className="text-sm text-white/30">
                  Screenshots coming soon
                </p>
              </div>
            )}
          </div>
        </AnimatedSection>

        {/* Bottom nav */}
        <AnimatedSection delay={0.4}>
          <div className="border-t border-white/[0.06] pt-8">
            <Link
              href="/#work"
              className={`inline-flex items-center gap-2 text-sm font-medium ${colors.text} transition-opacity hover:opacity-80`}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13 8H3M3 8L7 4M3 8L7 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back to all projects
            </Link>
          </div>
        </AnimatedSection>
      </div>

      {/* Screenshot lightbox */}
      <AnimatePresence>
        {lightbox !== null && shots[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm sm:p-8"
            onClick={() => setLightbox(null)}
            role="dialog"
            aria-modal="true"
            aria-label={`${project.name} screenshot ${lightbox + 1} of ${shots.length}`}
          >
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition-colors hover:bg-white/20"
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>

            {shots.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightbox((i) => (i === null ? i : (i - 1 + shots.length) % shots.length));
                }}
                className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition-colors hover:bg-white/20 sm:left-6"
                aria-label="Previous screenshot"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
            )}

            <motion.div
              key={lightbox}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="relative"
            >
              <Image
                src={shots[lightbox]}
                alt={`${project.name} screenshot ${lightbox + 1}`}
                width={400}
                height={869}
                className="max-h-[88vh] w-auto rounded-xl object-contain shadow-2xl ring-1 ring-white/10"
                unoptimized
                priority
              />
            </motion.div>

            {shots.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightbox((i) => (i === null ? i : (i + 1) % shots.length));
                }}
                className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition-colors hover:bg-white/20 sm:right-6"
                aria-label="Next screenshot"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            )}

            {shots.length > 1 && (
              <span className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs text-white/80 ring-1 ring-white/15">
                {lightbox + 1} / {shots.length}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default CaseStudy;
